// Calendar events, bucketed onto the days of a month grid.
//
// Kept apart from the component that draws it for the reason the billing
// calendar's model is: the two things that can actually be wrong here are
// arithmetic, not markup. Which day an event lands on, and what a day's figure
// comes to, are decided in this file and tested directly.
//
// ---------------------------------------------------------------------------
// Why this is not `pages/billing/calendarModel.ts`
// ---------------------------------------------------------------------------
//
// That one buckets ONE kind of row and reports a list of totals, one per
// currency, because it reads raw charges and has no rate to convert them
// with. This one buckets SEVEN kinds and reports a single figure, because the
// calendar report converts server-side and echoes the dated rate it applied
// (D6) — which is exactly the footnote the billing calendar has nowhere to put.
// Merging them would mean one model that sometimes converts and sometimes
// refuses to, keyed by which screen called it.
//
// ---------------------------------------------------------------------------
// What a day's figure counts, and what it does not
// ---------------------------------------------------------------------------
//
// BILLS ONLY. Not a cost recovery, whose headline figure is the PARTNER's share
// of a cost rather than a customer's gross; not implementation fees, which are
// suggestions nobody has committed to invoice; not a deal's contracted total,
// which is a year of revenue rather than a day's. Adding any of them would give
// a day a number that reconciles against nothing — the same rule, for the same
// reason, as the report's two separate totals blocks.

import type { CalendarEvent } from "@crm/shared";
import {
  monthGridWeeks,
  type CalendarMonth,
  type MonthGridDay,
} from "../../format/calendarDate.js";

export interface CalendarDay extends MonthGridDay {
  /** True only for the reader's own today — `todayIsoDate`, not UTC's. */
  isToday: boolean;
  /** Everything on this day, in the order the report returned them. */
  events: readonly CalendarEvent[];
  /** Gross across this day's `bill_due` events. See the header for why only those. */
  billGrossMinor: number;
  billCount: number;
}

export interface CalendarWeek {
  /** The week's first day, as a stable React key. */
  key: string;
  days: readonly CalendarDay[];
}

export interface CalendarMonthModel {
  month: CalendarMonth;
  weeks: readonly CalendarWeek[];
  /**
   * Over the days that BELONG to this month. The leading and trailing cells are
   * drawn and their events are real, but they belong to the neighbouring month's
   * total, and counting them here would double-count them the moment somebody
   * pages to that month.
   */
  monthEventCount: number;
  monthBillGrossMinor: number;
  monthBillCount: number;
}

/**
 * Bucket `events` onto the grid for `month`.
 *
 * Bucketing is a lookup on the raw `date` STRING — no `Date` is constructed for
 * an event at any point, so there is no instant for a reader's timezone to
 * shift. `2026-09-01` matches the cell whose `iso` is `2026-09-01`, in every
 * zone on earth.
 *
 * Events outside the grid's range are dropped rather than clamped onto an edge
 * cell: the caller queries exactly the grid's range, so their presence would
 * mean a stale cache rather than a month worth showing.
 */
export function buildEventCalendar(
  month: CalendarMonth,
  events: readonly CalendarEvent[],
  today: string,
): CalendarMonthModel {
  const byDate = new Map<string, CalendarEvent[]>();
  for (const event of events) {
    const bucket = byDate.get(event.date);
    if (bucket === undefined) byDate.set(event.date, [event]);
    else bucket.push(event);
  }

  let monthEventCount = 0;
  let monthBillGrossMinor = 0;
  let monthBillCount = 0;

  const weeks = monthGridWeeks(month).map((week): CalendarWeek => {
    const days = week.map((day): CalendarDay => {
      const dayEvents = byDate.get(day.iso) ?? [];
      let billGrossMinor = 0;
      let billCount = 0;
      for (const event of dayEvents) {
        if (event.kind !== "bill_due") continue;
        // Counted in `billCount` and left out of the money, since 0017: the day
        // has a bill on it and part of that day's figure is unknown. A 0 would
        // make the cell read as a complete total that happens to be smaller.
        if (event.gross_minor !== null) billGrossMinor += event.gross_minor;
        billCount += 1;
      }

      if (day.inMonth) {
        monthEventCount += dayEvents.length;
        monthBillGrossMinor += billGrossMinor;
        monthBillCount += billCount;
      }

      return { ...day, isToday: day.iso === today, events: dayEvents, billGrossMinor, billCount };
    });

    // Every week the grid produces has seven days, so `days[0]` is present; the
    // fallback keeps this total rather than relying on that.
    return { key: days[0]?.iso ?? `${month.year}-${month.month}`, days };
  });

  return { month, weeks, monthEventCount, monthBillGrossMinor, monthBillCount };
}

export interface CalendarAgendaDay {
  iso: string;
  isToday: boolean;
  isPast: boolean;
  events: readonly CalendarEvent[];
}

/**
 * The same events as a chronological list, one entry per day that HAS any.
 *
 * The agenda's whole advantage over the grid is that it skips empty days, so a
 * month with eleven events is eleven lines rather than a page of blank cells.
 * It therefore covers the full queried window rather than the month — including
 * the grid's leading and trailing days, which are days the reader can see and
 * would otherwise have to page to find.
 *
 * The events arrive sorted (the report's order is total), so this groups without
 * re-sorting: re-deriving the order client-side is how a list starts disagreeing
 * with the grid about which of two events on one day comes first.
 */
export function buildAgenda(
  events: readonly CalendarEvent[],
  today: string,
): readonly CalendarAgendaDay[] {
  const days: CalendarAgendaDay[] = [];
  for (const event of events) {
    const last = days[days.length - 1];
    if (last !== undefined && last.iso === event.date) {
      (last.events as CalendarEvent[]).push(event);
      continue;
    }
    days.push({
      iso: event.date,
      isToday: event.date === today,
      // Past days are dimmed rather than dropped: a bill that was due last week
      // and is still `ready` is the most urgent thing on the screen.
      isPast: event.date < today,
      events: [event],
    });
  }
  return days;
}
